import * as XLSX from 'xlsx';
import { parse, format, isValid } from 'date-fns';
import { GanttResult, GanttRow } from './types';
import { ColorCode } from '../../Types/Tasks';

const generateRandomColor = (): string => {
  const color = Math.floor(Math.random() * 0xffffff).toString(16);
  return `#${color.padStart(6, '0')}`;
};

const tryParseDate = (input: any): Date => {
  if (typeof input === 'number') {
    const parsed = XLSX.SSF.parse_date_code(input);
    if (parsed && parsed.y && parsed.m && parsed.d) {
      return new Date(parsed.y, parsed.m - 1, parsed.d);
    }
  }

  if (typeof input === 'string') {
    const formats = ['d/M/yyyy', 'dd/MM/yyyy', 'M/d/yyyy', 'MM/dd/yyyy', 'd/M/yy', 'dd/MM/yy', 'M/d/yy', 'MM/dd/yy'];
    for (const f of formats) {
      const parsed = parse(input, f, new Date());
      if (isValid(parsed)) {
        const year = parsed.getFullYear();
        if (year < 100) {
          const fixedYear = year < 50 ? 2000 + year : 1900 + year;
          parsed.setFullYear(fixedYear);
        }
        return parsed;
      }
    }
  }

  return new Date('');
};

const formatDateDDMMYYYY = (date: Date): string => {
  return format(date, 'dd/MM/yyyy');
};

export const parseGanttExcel = (
  fileBuffer: ArrayBuffer,
  colorCodes:  ColorCode[] = []
): GanttResult => {
  const workbook = XLSX.read(fileBuffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<any>(sheet, { defval: null });
  const headerRow = XLSX.utils.sheet_to_json<any>(sheet, {
    header: 1,
    range: 0,
  })[0] || [];

  if (!raw.length) return { data: [], errors: [] };

  const ID_COL = 'ID';
  const NAME_COL = 'Nombre';
  const metadataCols = [ID_COL, NAME_COL];

  const columnMap: Record<string, string> = {};

  headerRow.forEach((headerCell: any) => {
    if (!metadataCols.includes(headerCell)) {
      const parsedDate = tryParseDate(headerCell);
      if (isValid(parsedDate)) {
        const formatted = formatDateDDMMYYYY(parsedDate);
        columnMap[headerCell] = formatted;
      }
    }
  });

  const result: GanttRow[] = [];
  const codeColorMap: Record<string, { color: string; colorCodeId: string | null }> = {};

  let groupCounter = 0;
  let partidaCounter = 0;
  let currentGroupId: string | null = null;
  const hasExplicitIds = raw.some(row => row[ID_COL] != null);

  for (const row of raw) {
    const name = row[NAME_COL]?.toString().trim() ?? '';
    const explicitId = row[ID_COL] ? String(row[ID_COL]) : null;

    const dateValues = Object.entries(row)
      .filter(([key, value]) => !metadataCols.includes(key) && value != null)
      .map(([key, value]) => ({
        col: columnMap[key] ?? key,
        value,
      }));

    const nonEmptyCodes = dateValues.map(d => d.value).filter(Boolean);
    const isGroupRow = nonEmptyCodes.length === 0;

    if (isGroupRow) {
      const id = explicitId ?? `${++groupCounter}`;
      result.push({
        schema: 'group',
        id,
        name,
        group_id: null,
      });
      currentGroupId = id;
      partidaCounter = 0;
      continue;
    }

    const uniqueCodes = Array.from(new Set(nonEmptyCodes.map((v: any) => v.toString().trim())));
    const allParsedDates = dateValues
      .map(d => tryParseDate(d.col))
      .filter(isValid);
    if (allParsedDates.length === 0) continue;

    const start = formatDateDDMMYYYY(new Date(Math.min(...allParsedDates.map(d => d.getTime()))));
    const end = formatDateDDMMYYYY(new Date(Math.max(...allParsedDates.map(d => d.getTime()))));

    if (uniqueCodes.length === 1) {
      partidaCounter++;
      const id = explicitId ?? (currentGroupId ? `${currentGroupId}.${partidaCounter}` : `${++groupCounter}`);
      result.push({
        schema: 'task',
        type: 'partida',
        id,
        name,
        code: '',
        color: '',
        colorCodeId: null,
        start,
        end,
        group_id: currentGroupId ?? null,
      });
      continue;
    }

    // Parent Partida
    partidaCounter++;
    const partidaId = explicitId ?? (currentGroupId ? `${currentGroupId}.${partidaCounter}` : `${++groupCounter}`);
    result.push({
      schema: 'task',
      type: 'partida',
      id: partidaId,
      name,
      code: '',
      color: '',
      colorCodeId: null,
      start,
      end,
      group_id: currentGroupId ?? null,
    });

    uniqueCodes.forEach((code, index) => {
      if (typeof code !== 'string') return;
      const trimmedCode = code.trim();
      if (!trimmedCode) return;

      const matchingDates = dateValues
        .filter(d => {
          const cellVal = typeof d.value === 'string' ? d.value.trim() : d.value;
          return cellVal === trimmedCode;
        })
        .map(d => tryParseDate(d.col))
        .filter(isValid);

      if (matchingDates.length === 0) return;

      const trabajoStart = formatDateDDMMYYYY(new Date(Math.min(...matchingDates.map(d => d.getTime()))));
      const trabajoEnd = formatDateDDMMYYYY(new Date(Math.max(...matchingDates.map(d => d.getTime()))));

      if (!codeColorMap[trimmedCode]) {
        const existingColorEntry = colorCodes.find(c => c.name === trimmedCode);
        codeColorMap[trimmedCode] = {
          color: existingColorEntry?.color ?? generateRandomColor(),
          colorCodeId: existingColorEntry?._id ?? null,
        };
      }

      const { color, colorCodeId } = codeColorMap[trimmedCode];

      result.push({
        schema: 'task',
        type: 'trabajo',
        id: `${partidaId}.${index + 1}`,
        name: `${name} - ${trimmedCode}`,
        code: trimmedCode,
        color,
        colorCodeId,
        start: trabajoStart,
        end: trabajoEnd,
        parent_id: partidaId,
      });
    });
  }

  return { data: result, errors: [] };
};